/**
 * Inspired by https://github.com/rintoj/angular2-virtual-scroll
 * Could be optimized by reusing list item components instead of creating new ones while scrolling:
 *   https://developers.google.com/web/updates/2016/07/infinite-scroller
 */
import {Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {CuiScrollContainer} from '../shared/scroll-container';

interface Dimensions {
  firstItemIndex: number;
  lastItemIndex: number;
  firstItemPosition: number;
  listHeight: number;
}

@Component({
  selector: 'cui-virtual-scroll',
  templateUrl: './virtual-scroll.component.html',
  styleUrls: ['./virtual-scroll.component.scss']
})
export class CuiVirtualScrollComponent implements OnChanges, OnInit, OnDestroy {
  @Input() items: any[] = [];

  @Input() itemHeights: number | number[];

  @Input() disabled = false;

  // Number of screen heights that are left to scroll before reaching the bottom
  @Input() nearEndThreshold = 1;

  @Output() nearEnd = new EventEmitter<void>();

  @Output() visibleItems = new EventEmitter<any[]>();

  firstItemPosition: number;
  listHeight: number;

  private previousFirstItemIndex: number;
  private previousLastItemIndex: number;
  private hasEmittedNearEnd = false;
  private scrollSubscription: Subscription;

  constructor(private container: CuiScrollContainer) { }

  ngOnInit() {
    this.scrollSubscription = this.container.scroll.subscribe(() => this.calculateItems(false));
  }

  ngOnDestroy() {
    this.scrollSubscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items || changes.itemHeights) {
      this.calculateItems(true);
      this.hasEmittedNearEnd = false;
    }
  }

  private calculateItems(itemsHasChanged: boolean) {
    if (this.disabled) {
      return;
    }

    const { firstItemIndex, lastItemIndex, firstItemPosition, listHeight } = Array.isArray(this.itemHeights)
      ? this.calcDynamicDimensions()
      : this.calcStaticDimensions();

    this.firstItemPosition = firstItemPosition;
    this.listHeight = listHeight;

    const visibleItemsHasChanged = firstItemIndex !== this.previousFirstItemIndex || lastItemIndex !== this.previousLastItemIndex;

    if (itemsHasChanged || visibleItemsHasChanged) {
      const visibleItems = this.items.slice(firstItemIndex, lastItemIndex + 1);

      this.visibleItems.emit(visibleItems);

      this.previousFirstItemIndex = firstItemIndex;
      this.previousLastItemIndex = lastItemIndex;
    }

    // Wait for new list items to render before checking
    setTimeout(() => this.checkIfNearEnd());
  }

  private checkIfNearEnd() {
    if (this.items.length === 0) {
      return;
    }

    const viewHeight = this.container.getViewportHeight();
    const scrollHeight = this.container.getScrollHeight();
    const scrollPosition = this.container.getScrollPosition();

    const scrollThreshold = scrollHeight - viewHeight * (1 + this.nearEndThreshold);

    if (!this.hasEmittedNearEnd && scrollPosition > scrollThreshold) {
      this.nearEnd.emit();
      this.hasEmittedNearEnd = true;
    }
  }

  private calcStaticDimensions(): Dimensions {
    const itemCount = this.items.length;
    const itemHeights = <number> this.itemHeights;

    const viewHeight = this.container.getViewportHeight();
    const scrollPosition = this.container.getScrollPosition();

    // Find the index of the first and last visible items
    const firstItemIndex = Math.floor(scrollPosition / itemHeights);
    const lastItemIndex = Math.min(itemCount, Math.ceil(firstItemIndex + viewHeight / itemHeights));

    // Find the position of the first visible item in pixels
    const firstItemPosition = itemHeights * firstItemIndex;

    // The total height of the list in pixels
    const listHeight = itemHeights * itemCount;

    return { firstItemIndex, lastItemIndex, firstItemPosition, listHeight };
  }

  private calcDynamicDimensions(): Dimensions {
    const itemCount = this.items.length;
    const itemHeights = <number[]> this.itemHeights;

    const viewHeight = this.container.getViewportHeight();
    const scrollPosition = this.container.getScrollPosition();

    // Find the index of the first and last visible items
    const firstItemIndex = itemHeights.findIndex(height => height > scrollPosition);
    const indexOfLast = itemHeights.findIndex(height => height >= scrollPosition + viewHeight);
    const lastItemIndex = indexOfLast > 0 ? indexOfLast : itemCount;

    // Find the position of the first visible item in pixels
    const firstItemPosition = itemHeights[firstItemIndex - 1] ? itemHeights[firstItemIndex - 1] : 0;

    // The total height of the list in pixels
    const listHeight = itemHeights[itemHeights.length - 1];

    return { firstItemIndex, lastItemIndex, firstItemPosition, listHeight };
  }
}



// WEBPACK FOOTER //
// ./node_modules/@cicero/webui/lib/core/virtual-scroll/virtual-scroll.component.ts